import { ref, get, set, child, onValue, off } from 'firebase/database';
import type { DatabaseReference } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { db, isFirebaseActive, removeUndefined, getFirebaseAuth } from '../firebase';
import { useProductStore, defaultCategories, defaultProducts } from '../store/productStore';
import { useSettingsStore } from '../store/settingsStore';
import { useOrderStore } from '../store/orderStore';
import { useUserStore } from '../store/userStore';
import { useQuotationStore } from '../store/quotationStore';

let isInitializing = false;
let isInitialized = false;

/** Sanitize email for use as Firebase DB key */
export function sanitizeEmailForFirebase(email: string): string {
  return email.toLowerCase().replace(/\./g, '_');
}

// ─── Realtime listener refs (for cleanup) ───────────────────────
let _ordersRef: DatabaseReference | null = null;
let _emailsRef: DatabaseReference | null = null;
let _usersRef: DatabaseReference | null = null;
let _customerOrdersRef: DatabaseReference | null = null;
let _userProfileRef: DatabaseReference | null = null;
let _categoryProductsRef: DatabaseReference | null = null;  // per-category listener
let _categoriesRef: DatabaseReference | null = null;
let _quotationsRef: DatabaseReference | null = null;

function stopAllRealtimeSync() {
  if (_ordersRef) { off(_ordersRef); _ordersRef = null; }
  if (_emailsRef) { off(_emailsRef); _emailsRef = null; }
  if (_usersRef)  { off(_usersRef);  _usersRef = null; }
  if (_customerOrdersRef) { off(_customerOrdersRef); _customerOrdersRef = null; }
  if (_userProfileRef) { off(_userProfileRef); _userProfileRef = null; }
  if (_categoryProductsRef) { off(_categoryProductsRef); _categoryProductsRef = null; }
  if (_quotationsRef) { off(_quotationsRef); _quotationsRef = null; }
  // Note: _categoriesRef is permanent — stays alive for the session
}

/** Stop the current per-category products listener */
export function stopCategoryProductsListener() {
  if (_categoryProductsRef) { off(_categoryProductsRef); _categoryProductsRef = null; }
  useProductStore.setState({ currentCategoryId: null });
}

// ─── Diff tracking ───────────────────────────────────────────────
let prevCurrentUser: any = null;

/** Push a value to a Firebase node (fire-and-forget) */
export function syncNodeToFirebase(node: string, data: any) {
  if (!isFirebaseActive || !db) return;
  set(ref(db, node), removeUndefined(data)).catch((err) =>
    console.error(`Firebase write error [${node}]:`, err)
  );
}

// ─── Admin: attach realtime listeners ───────────────────────────
export function loadAdminDataSync() {
  if (!isFirebaseActive || !db) return;
  stopAllRealtimeSync();
  const database = db;

  console.log('[Firebase] Admin: attaching realtime listeners...');

  // Auto-populate categories in database if they don't exist and we are authenticated admin
  get(ref(database, 'categories')).then((snap) => {
    if (!snap.exists()) {
      console.log('[Firebase] Admin: Categories node is empty. Initializing database with defaults...');
      defaultCategories.forEach(cat => {
        set(ref(database, `categories/${cat.id}`), removeUndefined(cat)).catch(err => 
          console.error('[Firebase] Failed to auto-populate category:', err)
        );
      });
    }
  }).catch(err => {
    console.error('[Firebase] Admin: Failed to check categories existence:', err);
  });

  // Orders
  _ordersRef = ref(database, 'orders');
  onValue(_ordersRef, (snap) => {
    try {
      const val = snap.exists() ? snap.val() : {};
      const list: any[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
      const cleanList = list.map(order => {
        if (order && order.items) {
          order.items = (Array.isArray(order.items) ? order.items : Object.values(order.items)).filter(Boolean);
        }
        return order;
      });
      useOrderStore.setState({ orders: cleanList });
    } catch (e) { console.error('[Firebase] orders parse error:', e); }
  });

  // Emails
  _emailsRef = ref(database, 'emails');
  onValue(_emailsRef, (snap) => {
    try {
      const val = snap.exists() ? snap.val() : {};
      const list: any[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
      useOrderStore.setState({ emails: list });
    } catch (e) { console.error('[Firebase] emails parse error:', e); }
  });

  // Users
  _usersRef = ref(database, 'users');
  onValue(_usersRef, (snap) => {
    try {
      const val = snap.exists() ? snap.val() : {};
      const list: any[] = Object.values(val).map((u: any) => {
        const { orders: _o, ...profile } = u;
        if (profile.addresses) {
          profile.addresses = (Array.isArray(profile.addresses) ? profile.addresses : Object.values(profile.addresses)).filter(Boolean);
        } else {
          profile.addresses = [];
        }
        return profile;
      });
      useUserStore.setState({ registeredUsers: list });
    } catch (e) { console.error('[Firebase] users parse error:', e); }
  });

  // Quotations
  _quotationsRef = ref(database, 'quotations');
  onValue(_quotationsRef, (snap) => {
    try {
      const val = snap.exists() ? snap.val() : {};
      const list: any[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
      const cleanList = list.map(q => {
        if (q && q.items) {
          q.items = (Array.isArray(q.items) ? q.items : Object.values(q.items)).filter(Boolean);
        } else if (q) {
          q.items = [];
        }
        return q;
      }).sort((a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime());
      useQuotationStore.setState({ quotations: cleanList });
    } catch (e) { console.error('[Firebase] quotations parse error:', e); }
  });

  console.log('[Firebase] Admin realtime listeners attached.');
}

// ─── Customer: load profile + attach realtime orders listener ───
export async function loadCustomerDataSync() {
  if (!isFirebaseActive || !db) return;
  stopAllRealtimeSync();
  const database = db;

  const currentUser = useUserStore.getState().currentUser;
  if (!currentUser?.email) {
    return;
  }

  const sanitizedEmail = sanitizeEmailForFirebase(currentUser.email);
  console.log(`[Firebase] Customer: loading data for ${currentUser.email}...`);

  // Realtime profile listener
  _userProfileRef = ref(database, `users/${sanitizedEmail}`);
  onValue(_userProfileRef, (snap) => {
    try {
      if (snap.exists()) {
        const { orders: _o, ...profile } = snap.val();
        if (!profile.email && currentUser?.email) {
          profile.email = currentUser.email;
        }
        if (profile.addresses) {
          profile.addresses = (Array.isArray(profile.addresses) ? profile.addresses : Object.values(profile.addresses)).filter(Boolean);
        } else {
          profile.addresses = [];
        }
        useUserStore.setState({ currentUser: profile });
      }
    } catch (e) { console.error('[Firebase] profile parse error:', e); }
  });

  // Realtime orders listener
  _customerOrdersRef = ref(database, `users/${sanitizedEmail}/orders`);
  onValue(_customerOrdersRef, (snap) => {
    try {
      const val = snap.exists() ? snap.val() : {};
      const dbList: any[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
      const cleanDbList = dbList.map(order => {
        if (order && order.items) {
          order.items = (Array.isArray(order.items) ? order.items : Object.values(order.items)).filter(Boolean);
        }
        return order;
      });

      useOrderStore.setState({ orders: cleanDbList, emails: [] });
    } catch (e) { console.error('[Firebase] customer orders parse error:', e); }
  });

  console.log(`[Firebase] Customer listener attached for ${currentUser.email}.`);
}

// ─── Shared product parser ────────────────────────────────────────
function parseProductList(val: any): any[] {
  const list: any[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);
  const valid = list.filter(p => p && p.id && p.name && p.price !== undefined);
  const seen = new Set<string>();
  return valid
    .filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; })
    .map(p => {
      const rawImages = p.images
        ? (Array.isArray(p.images) ? p.images : Object.values(p.images)).filter(Boolean) as string[]
        : [];
      const cleanImages = rawImages.filter((img: string) => typeof img === 'string' && (img.startsWith('http') || img.startsWith('data:')));
      const imageUrl = p.imageUrl && typeof p.imageUrl === 'string' && (p.imageUrl.startsWith('http') || p.imageUrl.startsWith('data:'))
        ? p.imageUrl
        : null;
      const images = cleanImages.length > 0 ? cleanImages : (imageUrl ? [imageUrl] : []);
      return {
        ...p,
        categoryId: p.categoryId ? p.categoryId.toLowerCase() : '',
        subCategoryId: p.subCategoryId ? p.subCategoryId.toLowerCase() : '',
        imageUrl,
        images,
        specs: p.specs
          ? (Array.isArray(p.specs) ? p.specs : Object.values(p.specs)).filter(Boolean) as any[]
          : []
      };
    });
}

// ─── Lazy product loaders (called on-demand by pages) ────────────

/**
 * Load products for a specific category (cached in Zustand memory).
 * Since all products are fetched once and kept in memory, navigation requires zero network bandwidth.
 */
export async function loadProductsByCategory(categoryId: string) {
  const store = useProductStore.getState();
  if (store.isProductsLoaded) {
    useProductStore.setState({ currentCategoryId: categoryId });
    return;
  }
  
  useProductStore.setState({ isLoadingProducts: true, currentCategoryId: categoryId });
  await loadAllProducts();
  useProductStore.setState({ currentCategoryId: categoryId });
}

/**
 * Load ALL products (cached in Zustand memory) using a single get() request.
 * Serves localStorage cache instantly for speed, then ALWAYS does a silent
 * background refresh from Firebase to ensure no products are ever missed.
 *
 * Fix: Previously, once isProductsLoaded=true the function returned early and
 * never re-fetched from Firebase. This caused stale localStorage caches to hide
 * products that existed in Firebase — regular users saw fewer search results
 * than admins (who would trigger a fresh session without the stale cache).
 */
export async function loadAllProducts() {
  const store = useProductStore.getState();

  // If already serving cached products, we still do a silent background refresh
  // to ensure the store always reflects the full Firebase catalog.
  if (store.isProductsLoaded) {
    useProductStore.setState({ currentCategoryId: 'all' });
    console.log(`[RTDB] Cache hit: ${store.products.length} items. Starting silent background refresh...`);
    // Silent background refresh — updates store if Firebase has more/different products
    _silentRefreshProducts();
    return;
  }

  if (!isFirebaseActive || !db) {
    // Offline mode fallback
    useProductStore.setState({ 
      products: defaultProducts, 
      isProductsLoaded: true,
      isLoadingProducts: false, 
      currentCategoryId: 'all' 
    });
    return;
  }

  stopCategoryProductsListener();
  useProductStore.setState({ isLoadingProducts: true, currentCategoryId: 'all' });

  try {
    const productsSnap = await get(ref(db, 'products'));

    if (!productsSnap.exists()) {
      useProductStore.setState({ products: [], isProductsLoaded: true, isLoadingProducts: false });
      return;
    }

    const parsed = parseProductList(productsSnap.val());

    // NOTE: We no longer fetch 'productImages' in bulk to avoid downloading all images at once.
    // Components like ProductImage.tsx will lazy-load images on-demand.

    try {
      localStorage.setItem('sensorzone_products_cache', JSON.stringify(parsed));
    } catch (e) {}

    useProductStore.setState({ 
      products: parsed, 
      isProductsLoaded: true, 
      isLoadingProducts: false 
    });
    console.log(`[RTDB] Catalog downloaded in 1 fast query: ${parsed.length} items.`);
  } catch (e) {
    console.error('[RTDB] Catalog load error:', e);
    useProductStore.setState({ isLoadingProducts: false });
  }
}

/**
 * Silent background product refresh — fetches latest products from Firebase
 * and updates the store/cache only if the data has changed (more products or
 * different content). This fixes stale localStorage caches without blocking UI.
 */
let _silentRefreshInProgress = false;
async function _silentRefreshProducts() {
  if (_silentRefreshInProgress || !isFirebaseActive || !db) return;
  _silentRefreshInProgress = true;
  try {
    const productsSnap = await get(ref(db, 'products'));
    if (!productsSnap.exists()) return;
    const parsed = parseProductList(productsSnap.val());
    
    // Compare stringified data to detect ANY changes (not just count changes)
    const parsedString = JSON.stringify(parsed);
    const currentCache = localStorage.getItem('sensorzone_products_cache');
    
    if (parsedString !== currentCache) {
      console.log(`[RTDB] Silent refresh: updating catalog from Firebase.`);
      try {
        localStorage.setItem('sensorzone_products_cache', parsedString);
      } catch (e) {}
      useProductStore.setState({ products: parsed });
    } else {
      console.log(`[RTDB] Silent refresh: catalog up to date (${parsed.length} products).`);
    }
  } catch (e) {
    console.warn('[RTDB] Silent background refresh failed:', e);
  } finally {
    _silentRefreshInProgress = false;
  }
}

/**
 * Load featured products for Home page. Instant zero-wait cache load.
 */
export async function loadFeaturedProducts() {
  const store = useProductStore.getState();
  if (store.products.length > 0) {
    useProductStore.setState({ currentCategoryId: '__home__', isLoadingProducts: false });
  }
  // Quiet background sync for latest Firebase updates
  loadAllProducts();
}

// ─── Main initialization ─────────────────────────────────────────
export async function initializeDatabaseSync() {

  if (!isFirebaseActive || !db) {
    console.log('[Firebase] Disabled — running in offline mode.');
    useProductStore.setState({ 
      products: defaultProducts, 
      categories: defaultCategories, 
      isLoadingProducts: false, 
      isLoadingCategories: false 
    });
    return;
  }

  if (isInitialized || isInitializing) {
    console.log('[Firebase] Sync already initialized or initializing.');
    return;
  }

  console.log('[Firebase] Initialising sync...');
  isInitializing = true;
  const database = db;

  try {
    // 1. Settings parsing function
    const parseSettings = (s: any) => {
      if (!s) return;
      const isHtml = (text: string) => /<[a-z][\s\S]*>/i.test(text || '');
      const DEFAULT_ORDER_CONFIRMATION = {
        subject: 'Order Confirmation - {{orderId}}',
        body: 'Dear {{customerName}},\n\nThank you for your order! Your order {{orderId}} has been successfully received.\n\nPlease complete your payment by transferring the full amount of LKR {{total}} to our bank account. We will begin preparing your parcel as soon as the payment transaction is received.\n\nBank Account Details (Commercial Bank):\n- Bank Name: Commercial Bank\n- Branch: Colombo Fort\n- Account Number: 8002345678\n- Account Name: SensorZone (Pvt) Ltd\n\nOnce payment is complete, please upload/WhatsApp your transaction receipt to +{{whatsappNumber}} along with your Order ID.\n\nBest regards,\nSensorZone.lk Team'
      };
      const DEFAULT_ORDER_CONFIRMATION_COD = {
        subject: 'Order Confirmation (COD) - {{orderId}}',
        body: 'Dear {{customerName}},\n\nThank you for your order! Your order {{orderId}} has been successfully received.\n\nWe will deliver your parcel via courier. Please pay the cash amount of LKR {{total}} to the courier when the package is delivered to your address.\n\nDelivery Address:\n{{deliveryAddress}}\n\nIf you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nBest regards,\nSensorZone.lk Team'
      };
      const DEFAULT_PAYMENT_RECEIVED = {
        subject: 'Payment Received & Processing - {{orderId}}',
        body: 'Dear {{customerName}},\n\nThank you for choosing SensorZone.lk! We have received your payment for order {{orderId}}.\n\nWe have started preparing your parcel and will make sure it is delivered to you as quickly as possible.\n\nThank you again for choosing us, we appreciate your business and will ensure you receive the items soon.\n\nBest regards,\nSensorZone.lk Team'
      };
      const DEFAULT_ORDER_PROCESSING_COD = {
        subject: 'Order Confirmed & Processing (COD) - {{orderId}}',
        body: 'Dear {{customerName}},\n\nThank you for choosing SensorZone.lk!\n\nYour Cash on Delivery order {{orderId}} has been confirmed and is now being processed. We are preparing your items for delivery.\n\nYou will pay the cash amount of LKR {{total}} to the courier when the package is delivered to your address.\n\nDelivery Address:\n{{deliveryAddress}}\n\nIf you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nBest regards,\nSensorZone.lk Team'
      };
      const DEFAULT_ORDER_DISPATCHED = {
        subject: 'Your Order {{orderId}} Has Been Dispatched! 🚀',
        body: 'Dear {{customerName}},\n\nGreat news! Your order {{orderId}} has been dispatched and is on its way to you.\n\n📦 *Tracking Details*:\n- Tracking Number: {{trackingNumber}}\n- Delivery Address: {{deliveryAddress}}\n\nYou can expect delivery within 1-3 business days. If you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nThank you for choosing SensorZone.lk!\n\nBest regards,\nSensorZone.lk Team'
      };

      let emailTemplates = s.emailTemplates ? { ...s.emailTemplates } : {
        orderConfirmation: DEFAULT_ORDER_CONFIRMATION,
        orderConfirmationCOD: DEFAULT_ORDER_CONFIRMATION_COD,
        paymentReceived: DEFAULT_PAYMENT_RECEIVED,
        orderProcessingCOD: DEFAULT_ORDER_PROCESSING_COD,
        orderDispatched: DEFAULT_ORDER_DISPATCHED
      };

      let changed = false;
      if (!emailTemplates.orderConfirmation || isHtml(emailTemplates.orderConfirmation?.body) || emailTemplates.orderConfirmation?.body?.includes('50%')) {
        emailTemplates.orderConfirmation = DEFAULT_ORDER_CONFIRMATION;
        changed = true;
      }
      if (!emailTemplates.orderConfirmationCOD || isHtml(emailTemplates.orderConfirmationCOD?.body)) {
        emailTemplates.orderConfirmationCOD = DEFAULT_ORDER_CONFIRMATION_COD;
        changed = true;
      }
      if (!emailTemplates.paymentReceived || isHtml(emailTemplates.paymentReceived?.body)) {
        emailTemplates.paymentReceived = DEFAULT_PAYMENT_RECEIVED;
        changed = true;
      }
      if (!emailTemplates.orderProcessingCOD || isHtml(emailTemplates.orderProcessingCOD?.body)) {
        emailTemplates.orderProcessingCOD = DEFAULT_ORDER_PROCESSING_COD;
        changed = true;
      }
      if (!emailTemplates.orderDispatched || isHtml(emailTemplates.orderDispatched?.body)) {
        emailTemplates.orderDispatched = DEFAULT_ORDER_DISPATCHED;
        changed = true;
      }

      let contactEmail = s.contactEmail ?? '';
      if (contactEmail.toLowerCase() === 'info@sensorzone.lk' || !contactEmail) {
        contactEmail = 'sensorzonetronic@gmail.com';
        changed = true;
      }

      const defaultPaymentMethods = [
        { id: 'bank', name: 'Bank Transfer / Deposit', description: 'Direct bank deposit or electronic fund transfer required.', enabled: true },
        { id: 'cod', name: 'Cash on Delivery (COD)', description: 'Pay with cash upon package delivery.', enabled: true }
      ];

      useSettingsStore.setState({
        carouselSlides:      s.carouselSlides      ?? [],
        deliverySettings:    {
          baseFee: s.deliverySettings?.baseFee ?? 350,
          freeThreshold: s.deliverySettings?.freeThreshold ?? 4990,
          codBaseFee: s.deliverySettings?.codBaseFee ?? 450,
        },
        whatsappNumber:      s.whatsappNumber      ?? '',
        hotlineNumber:       s.hotlineNumber       ?? '',
        contactEmail:        contactEmail,
        contactAddress:      s.contactAddress      ?? '',
        promoText:           s.promoText           ?? '',
        socialLinks:         s.socialLinks         ?? { facebook: '', instagram: '', tiktok: '', telegram: '', linkedin: '' },
        wholesaleInquiries:  s.wholesaleInquiries  ?? [],
        customInquiries:     s.customInquiries     ?? [],
        emailTemplates,
        aiBuilderName:       s.aiBuilderName       ?? '✨ AI Project Builder',
        industrialProjectsName: s.industrialProjectsName ?? '⚙️ Industrial Automation & Projects',
        paymentMethods:      s.paymentMethods      ?? defaultPaymentMethods,
      });

      if (changed) {
        set(ref(database, 'settings/emailTemplates'), emailTemplates).catch((err) =>
          console.error('[Firebase] Failed to write cleaned templates:', err)
        );
        set(ref(database, 'settings/contactEmail'), contactEmail).catch((err) =>
          console.error('[Firebase] Failed to write updated contactEmail:', err)
        );
      }
    };

    // Load Settings from RTDB (lightweight)
    get(child(ref(database), 'settings')).then((settingsSnap) => {
      if (settingsSnap.exists()) {
        parseSettings(settingsSnap.val());
      } else {
        const s = useSettingsStore.getState();
        set(ref(database, 'settings'), {
          carouselSlides: s.carouselSlides,
          deliverySettings: s.deliverySettings,
          whatsappNumber: s.whatsappNumber,
          hotlineNumber: s.hotlineNumber,
          contactEmail: s.contactEmail,
          contactAddress: s.contactAddress,
          promoText: s.promoText,
          socialLinks: s.socialLinks,
          wholesaleInquiries: s.wholesaleInquiries,
          customInquiries: s.customInquiries,
          emailTemplates: s.emailTemplates,
          aiBuilderName: s.aiBuilderName,
          industrialProjectsName: s.industrialProjectsName,
          paymentMethods: s.paymentMethods,
        }).catch(err => console.error('[Firebase] Settings init write error:', err));
      }
    });

    // 2. Setup RTDB real-time listener for categories ONLY (lightweight ~2KB)
    console.log('[RTDB] Registering real-time listener for categories...');

    _categoriesRef = ref(database, 'categories');
    onValue(_categoriesRef, (snap) => {
      try {
        if (!snap.exists()) {
          console.log('[Firebase] No categories found in database. Using local default categories.');
          useProductStore.setState({ categories: defaultCategories, isLoadingCategories: false });
          return;
        }
        const val = snap.val();
        const list: any[] = Array.isArray(val) ? val.filter(Boolean) : Object.values(val);

        const valid = list.filter(c => c && c.id && c.name);
        const seen = new Set<string>();
        const deduped: any[] = [];
        valid.forEach((c) => {
          const normalizedId = c.id.toLowerCase();
          if (!seen.has(normalizedId)) {
            seen.add(normalizedId);
            deduped.push({
              ...c,
              id: normalizedId,
              subCategories: c.subCategories
                ? (Array.isArray(c.subCategories) ? c.subCategories : Object.values(c.subCategories)).filter(Boolean)
                : []
            });
          } else {
            const existing = deduped.find(x => x.id === normalizedId);
            if (existing && c.subCategories) {
              const subs = (Array.isArray(c.subCategories) ? c.subCategories : Object.values(c.subCategories)).filter(Boolean);
              subs.forEach((s: any) => {
                if (!existing.subCategories.some((exSub: any) => exSub.id.toLowerCase() === s.id.toLowerCase())) {
                  existing.subCategories.push(s);
                }
              });
            }
          }
        });

        useProductStore.setState({ categories: deduped, isLoadingCategories: false });
        console.log(`[RTDB] Categories loaded: ${deduped.length} items`);
      } catch (e) {
        console.error('[RTDB] Categories parsing error:', e);
        useProductStore.setState({ isLoadingCategories: false });
      }
    }, (err) => {
      console.error('[RTDB] Categories listener failed, falling back to default categories:', err);
      useProductStore.setState({ categories: defaultCategories, isLoadingCategories: false });
    });

    // 3. Role-based realtime sync (after auth state is known)
    const setupRoleSync = async () => {
      const auth = getFirebaseAuth();
      const isAdmin = sessionStorage.getItem('admin_authorized') === 'true' || (auth?.currentUser?.email === 'sensorzonetronic@gmail.com');
      if (isAdmin) {
        loadAdminDataSync();
      } else {
        await loadCustomerDataSync();
      }
    };

    const authInstance = getFirebaseAuth();
    if (authInstance) {
      onAuthStateChanged(authInstance, async () => {
        if (isInitialized) await setupRoleSync();
      });
    }

    await setupRoleSync();
    console.log('[Firebase] Sync initialised successfully.');
    isInitialized = true;
  } catch (err) {
    console.error('[Firebase] Init error:', err);
  } finally {
    isInitializing = false;
  }
}

// ─── Store subscriptions (write-back to Firebase) ────────────────
// These run once the module is imported. They are guarded by isInitializing.

if (isFirebaseActive && db) {

  // Settings
  useSettingsStore.subscribe((state) => {
    if (isInitializing) return;
    syncNodeToFirebase('settings', {
      carouselSlides: state.carouselSlides,
      deliverySettings: state.deliverySettings,
      whatsappNumber: state.whatsappNumber,
      hotlineNumber: state.hotlineNumber,
      contactEmail: state.contactEmail,
      contactAddress: state.contactAddress,
      promoText: state.promoText,
      socialLinks: state.socialLinks,
      wholesaleInquiries: state.wholesaleInquiries,
      customInquiries: state.customInquiries,
      emailTemplates: state.emailTemplates,
      aiBuilderName: state.aiBuilderName,
      industrialProjectsName: state.industrialProjectsName,
      paymentMethods: state.paymentMethods,
    });
  });

  // Orders + Emails sync loop removed. Writes now happen directly in orderStore.ts actions.

  // Users (login/logout detection only; profile writes happen directly in userStore.ts actions)
  useUserStore.subscribe((state) => {
    if (isInitializing) return;

    // If admin is authorized, do not trigger customer sync
    const auth = getFirebaseAuth();
    const isAdmin = sessionStorage.getItem('admin_authorized') === 'true' || (auth?.currentUser?.email === 'sensorzonetronic@gmail.com');
    if (isAdmin) return;

    // Login / logout detection for realtime listeners
    const newUser = state.currentUser;
    if (newUser && (!prevCurrentUser || prevCurrentUser.email !== newUser.email)) {
      loadCustomerDataSync();
    } else if (!newUser && prevCurrentUser) {
      stopAllRealtimeSync();
      useOrderStore.setState({ orders: [], emails: [] });
    }
    prevCurrentUser = newUser;
  });
}
